import { type HskBand, hskBandLevel, hskBandOf } from "../hsk/hsk-band.js";

/**
 * The published index of 词典 addresses, as one line per address.
 *
 * The build writes this file and a page in the browser reads it back, and the
 * two have to agree about the separator, the column order and how a band is
 * spelled. Both ends import this module for that reason. See
 * `src/assets/cidian-index.ts` for the writing and `src/segmenting/cidian-index.ts`
 * for the reading.
 *
 * **The format is the smallest thing that answers two questions.** A page
 * segmenting a text has to know which of the words it found have an entry, so
 * that it links those and leaves the rest as plain text, and it has to know
 * what band an entry carries. Addresses alone are 97.8 KB over the 15,353
 * entries on disk and 32.5 KB brotlied. The band column takes that to 124.4 KB
 * and 44.8 KB, beside a dictionary tier of about 400 KB.
 */

/** What separates an address from the band beside it. */
export const columnSeparator = "\t";

/** The section 词典 entries are published under. */
export const cidianAddressPrefix = "/cidian/";

/** One published address, and the band the entry at it carries. */
export interface CidianAddress {
  /** The headword, as the address spells it. */
  readonly word: string;

  /** The band, where the entry is on the HSK scale at all. */
  readonly band: HskBand | undefined;

  /** The canonical headword the address resolves to. */
  readonly headword?: string;
}

/** Why an address could not be written as a line. */
export class CidianIndexLineError extends Error {}

/**
 * The address a word's entry is published at.
 *
 * Encoded, because these are Han characters in a path and a browser writing
 * the link into the address bar percent-encodes them anyway. Doing it here
 * means the `href` in the markup says what the address bar will.
 */
export function cidianAddress(word: string): string {
  return `${cidianAddressPrefix}${encodeURIComponent(word)}/`;
}

/**
 * One address as a line, or a refusal naming what stopped it.
 *
 * A tab or a newline in a headword would split one line into two columns or
 * two lines, and the reader at the far end would take whatever came out. No
 * headword in the section holds either, and this is the check rather than the
 * assumption.
 */
export function cidianIndexLine(address: CidianAddress): string {
  if (address.word.includes(columnSeparator) || address.word.includes("\n")) {
    throw new CidianIndexLineError(
      `${address.word} has a tab or a newline in it, which the address index ` +
        "cannot carry. See src/content/cidian/cidian-index-line.ts.",
    );
  }

  const band = address.band === undefined ? "" : hskBandLevel(address.band);
  const headword =
    address.headword === undefined || address.headword === address.word
      ? ""
      : `${columnSeparator}${address.headword}`;

  return band === "" && headword === ""
    ? address.word
    : `${address.word}${columnSeparator}${band}${headword}`;
}

/**
 * The whole index, as the addresses it holds and the band against each.
 *
 * A word with an entry and no band is in the map with nothing under it, so
 * asking whether there is an entry is `has` and asking for the band is `get`.
 * Collapsing the two would make an ungraded entry indistinguishable from a
 * word the section has never had.
 *
 * A line this cannot read is dropped. What the index is for is deciding
 * whether to draw a link, and a file that has been truncated in transit
 * should cost a reader some links rather than the tool.
 */
export function readCidianIndex(
  text: string,
): ReadonlyMap<string, HskBand | undefined> {
  const addresses = new Map<string, HskBand | undefined>();

  for (const line of text.split("\n")) {
    const [word, level] = line.split(columnSeparator);

    if (word !== undefined && word !== "") {
      addresses.set(
        word,
        level === undefined || level === "" ? undefined : hskBandOf(level),
      );
    }
  }

  return addresses;
}

/** The canonical headword behind each published address. */
export function readCidianHeadwords(text: string): ReadonlyMap<string, string> {
  const headwords = new Map<string, string>();

  for (const line of text.split("\n")) {
    const [word, , headword] = line.split(columnSeparator);

    if (word !== undefined && word !== "") {
      headwords.set(
        word,
        headword === undefined || headword === "" ? word : headword,
      );
    }
  }

  return headwords;
}
